import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import type { CatalogCategory } from '@okmaps/shared';
import { useAppSelector } from '../../app/hooks.js';
import { selectCategory, selectSearchText } from '../../store/selectors/index.js';
import {
  categorySelected,
  renderableOnlyToggled,
  searchTextChanged,
} from '../../store/slices/uiSlice.js';

interface Props {
  readonly categories: readonly CatalogCategory[];
  readonly renderableOnly: boolean;
  readonly dispatch: (action: unknown) => void;
}

export default function CatalogFilters({ renderableOnly, dispatch }: Props) {
  const searchText = useAppSelector(selectSearchText);
  const category = useAppSelector(selectCategory);

  return (
    <Box sx={{ p: 1.5, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
      <TextField
        fullWidth
        size="small"
        value={searchText}
        onChange={(e) => dispatch(searchTextChanged(e.target.value))}
        placeholder="Search datasets"
        slotProps={{
          // The accessible name must be on the <input> itself. Passing
          // aria-label to TextField puts it on the wrapping div, which leaves
          // the control unnamed for a screen reader (WCAG 4.1.2). A placeholder
          // is not a substitute: it disappears as soon as the user types.
          htmlInput: { 'aria-label': 'Search datasets by name, description, or keyword' },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchText ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => dispatch(searchTextChanged(''))}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          },
        }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, minHeight: 32 }}>
        {category ? (
          <Chip
            size="small"
            label={category}
            onDelete={() => dispatch(categorySelected(null))}
            color="primary"
            variant="outlined"
          />
        ) : null}

        <Box sx={{ flex: 1 }} />

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={renderableOnly}
              onChange={() => dispatch(renderableOnlyToggled())}
            />
          }
          label="Mappable only"
          slotProps={{ typography: { variant: 'caption' } }}
          sx={{ mr: 0 }}
        />
      </Box>
    </Box>
  );
}
